/**
 * editorPanelExtended.ts — надписи РАСШИРЕННОЙ панели свойств блока
 * (`components/editor/BlockPropertiesPanelExtended.tsx`, WEB-1874, пачка 15).
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ СЛОВАРЬ, А НЕ `editorPanel`. Панелей свойств две: основная
 * (`BlockPropertiesPanel.tsx`) и эта — на 5 000 строк и 27 типов блоков.
 * Волной переноса они ехали параллельно двумя агентами, и один файл словаря
 * на двоих означал бы драку за одни и те же строки. Общие подписи («Цвет»,
 * «Как в теме», «Удалить») сейчас есть в обоих; свести их в один ключ — работа
 * приёмки пачки, а не повод задержать перенос.
 *
 * ФОРМА ОБРАЩЕНИЯ. `renderExtendedPropertiesForm` — ПЛОСКАЯ функция без
 * hooks (так задумано: модал и состояние живут в родителе), поэтому язык
 * берётся `pickStrings`, а не `useStrings`. Перерисовка при смене языка
 * приходит сверху — из перерисовки самой панели.
 *
 * ЧТО НЕ ПЕРЕВОДИТСЯ. Имена собственные (Stripe, YouTube, Vimeo, BMI/ИМТ как
 * термин шкалы), коды значений ветвления (disagree/unsure/agree, no/yes) и
 * образцы содержимого воронки — они принадлежат воронке, а не кабинету.
 */
import { defineDictionary } from '../define';

export const editorPanelExtendedStrings = defineDictionary(
  {
    // ── Общая лексика панели (встречается у многих блоков) ────────────────
    color: 'Color',
    textColor: 'Text color',
    fillColor: 'Fill color',
    borderColor: 'Border color',
    itemFill: 'Element fill',
    themeDefault: 'Theme default',
    sameAsBlock: 'Same as block',
    auto: 'Auto',
    emptyFromPalette: 'Empty = from the palette',
    emptyThemeAccent: 'Empty = theme accent',
    emptyNoFill: 'Empty = no fill',
    emptyWhite: 'Empty = white',
    emptyFunnelAccent: 'Empty = funnel accent',
    emptyEmojiFallback: 'Empty = emoji placeholder',
    delete: 'Delete',
    show: 'Show',
    hide: 'Hide',
    alignLeft: 'Left',
    alignCenter: 'Center',
    alignRight: 'Right',
    alignment: 'Alignment',
    required: 'Required',
    minValue: 'Min value',
    maxValue: 'Max value',
    placeholderLabel: 'Placeholder',
    question: 'Question',
    answer: 'Answer',
    name: 'Name',
    heightPx: 'Height (px)',
    spacerMode: 'Mode',
    spacerModeFixed: 'Fixed',
    spacerModeFlexible: 'Fill free space',
    spacerMinHeightPx: 'Min height (px)',
    spacerFlexibleHint:
      'Takes all free space on the screen and pushes the blocks below to the bottom. Adds no scroll when space runs out. The screen vertical alignment does not apply.',
    widthPx: 'Width (px)',
    step: 'Step',
    buttonText: 'Button text',
    imageUrl: 'Image URL',
    regenerate: 'Regenerate',
    autoAdvance: 'Go to the next screen right after the choice',
    buttonLabelColor: 'Button label color',
    buttonLabelSize: 'Button label size, px',

    // ── Разделитель ───────────────────────────────────────────────────────
    dividerLineStyle: 'Line style',
    dividerSolid: 'Solid',
    dividerDashed: 'Dashed',
    dividerEdges: 'Edges',
    edgesSquare: 'Square',
    edgesRounded: 'Rounded',
    dividerFullWidth: 'full width',

    // ── Иконка и маркированный список ─────────────────────────────────────
    iconLabel: 'Icon',
    iconStar: 'Star',
    iconHeart: 'Heart',
    iconCheck: 'Check',
    iconInfo: 'Info',
    iconWarning: 'Warning',
    iconFire: 'Fire',
    iconBolt: 'Lightning',
    iconGift: 'Gift',
    iconLock: 'Lock',
    iconShield: 'Shield',
    iconMagnifier: 'Magnifier',
    iconClock: 'Clock',
    iconCalendar: 'Calendar',
    iconMoney: 'Money',
    iconChart: 'Chart',
    iconTrophy: 'Trophy',
    iconRocket: 'Rocket',
    iconBulb: 'Lightbulb',
    iconPhone: 'Phone',
    iconMail: 'Envelope',
    iconClip: 'Paperclip',
    listItems: 'Items',
    listAddItem: 'Add item',

    // ── FAQ (аккордеон) ───────────────────────────────────────────────────
    faqItems: 'FAQ questions',
    faqAddItem: 'Add FAQ question',
    faqRolesHint:
      'The settings apply to ALL items at once. Empty — as now: 16px bold heading, 15px answer in the muted theme color.',
    faqTitleColor: 'Item heading color',
    faqTitleSize: 'Item heading size, px',
    faqBodyColor: 'Expanded text color',
    faqBodySize: 'Expanded text size, px',

    // ── Две колонки ───────────────────────────────────────────────────────
    columnsLeftTitle: 'Left column heading',
    columnsLeftContent: 'Left column content',
    columnsLeftContentPlaceholder: 'Left column content',
    columnsLeftAlign: 'Left column alignment',
    columnsRightTitle: 'Right column heading',
    columnsRightContent: 'Right column content',
    columnsRightContentPlaceholder: 'Right column content',
    columnsRightAlign: 'Right column alignment',
    columnsRolesHint:
      'The setting applies to a text role, not to a column: one pair of values colors the headings of BOTH columns, the other — the content of both. Empty — as now.',
    columnsTitleColor: 'Column heading color',
    columnsTitleSize: 'Column heading size, px',
    columnsBodyColor: 'Column content color',
    columnsBodySize: 'Column content size, px',

    // ── Карточка ──────────────────────────────────────────────────────────
    cardTitle: 'Card heading',
    cardDescription: 'Description',
    cardDescriptionPlaceholder: 'Card description',
    cardImageUrl: 'Image URL (optional)',
    imageChoiceOptionGone:
      'The generated picture was not applied: this answer option no longer exists.',
    imageChoiceAutoAdvanceSingleOnly:
      'Only for single choice: with multiple choice the visitor taps Continue.',
    cardVariantLabel: 'Card look',
    cardVariantSolid: 'Solid',
    cardVariantGlass: 'Glass',
    // WEB-3243: картинка карточки сверху или фоном под текстом.
    cardImageModeLabel: 'Image',
    cardImageModeTop: 'on top',
    cardImageModeBackground: 'as background',
    cardImageModeGlassHint:
      'With the image as background the card look (Glass) does not apply: the image is the backing.',
    cardRolesHint:
      'Empty — as now: 18px heading, 15px description in the muted theme color.',
    cardTitleColor: 'Card heading color',
    cardTitleSize: 'Card heading size, px',
    cardDescriptionColor: 'Description color',
    cardDescriptionSize: 'Description size, px',

    // ── Поля ввода: дата, число, выпадающий список ────────────────────────
    datePlaceholder: 'Pick a date',
    numberPlaceholder: 'Enter a number',
    selectPlaceholder: 'Choose...',
    selectOptions: 'Options',
    selectAddOption: 'Add option',

    // ── Оценка звёздами ───────────────────────────────────────────────────
    ratingLabel: 'Label',
    ratingLabelPlaceholder: 'Rate your experience',
    ratingMaxStars: 'Max number of stars',
    ratingStarsAlign: 'Star alignment',
    ratingStarColor: 'Star color',
    ratingStarSize: 'Star size, px',
    ratingStarsHint:
      'An unfilled star takes the same color but stays paler — that is how the chosen score reads. The label text is set by the “Font size” and “Text color inside the element” fields.',

    // ── Ползунок ──────────────────────────────────────────────────────────
    sliderLabelColor: 'Label color',
    sliderLabelSize: 'Label size, px',
    sliderNumbersColor: 'Color of the numbers under the slider',
    sliderNumbersSize: 'Size of the numbers under the slider, px',

    // ── Чекбокс, имя, видео ───────────────────────────────────────────────
    checkboxDefaultState: 'Default state',
    namePlaceholder: 'Your name',
    videoUrl: 'Video URL (YouTube / Vimeo)',
    videoStart: 'Start',
    videoStartOnClick: 'On click',
    videoStartOnShow: 'On display',
    videoSound: 'Sound',
    videoWithSound: 'With sound',
    videoMuted: 'Muted',
    videoAutoplayMuted: 'Silent autoplay',
    videoSoundHint:
      'When the screen plays automatically, browsers always start the video muted — “With sound” applies to click-to-play.',
    videoClickUnmute: 'A click on the video turns the sound on',
    videoProgressBar: 'Seeking (progress bar)',
    videoOnShowHint:
      'With “On display” the sound is always off: browsers allow autoplay only without sound. The viewer can turn it on — or set the start to “On click”.',
    // Слово владельца 21.10.2026: ролик-файл (mp4/webm) — подсказка адреса и
    // вид ролика. Заполнения videoFit<Значение> сверяет сторож
    // video-file.ends.spec.ts с перечнем схемы.
    videoUrlHint:
      'A YouTube link (regular, youtu.be, Shorts, embed) or a Vimeo link, or a direct link to a video file (.mp4, .webm, .mov, .m4v).',
    videoFileSection: 'Video file',
    videoLoop: 'Loop',
    videoFit: 'Fit',
    videoFitCover: 'Fill (crop)',
    videoFitContain: 'Whole video',
    videoBoxHeight: 'Height, px',
    videoBoxHeightAuto: 'By video',

    // ── Отзывы ────────────────────────────────────────────────────────────
    testimonialsItems: 'Testimonials',
    testimonialsAdd: 'Add testimonial',
    testimonialName: 'Name',
    testimonialText: 'Testimonial text',
    testimonialRating: 'Score (1-5)',
    testimonialsRolesHint:
      'The setting applies to ALL testimonials of the block at once — same as the testimonial card border. The score stars are not affected.',
    testimonialAuthorColor: 'Author name color',
    testimonialAuthorSize: 'Author name size, px',
    testimonialTextColor: 'Testimonial text color',
    testimonialTextSize: 'Testimonial text size, px',
    // Слово владельца 21.10.2026: фото автора, отметка и вид карточки отзыва.
    testimonialPhotoUrl: 'Author photo URL',
    testimonialPhotoUrlInvalid:
      'This address is not accepted: use a https:// link or a path to the file.',
    testimonialBadge: 'Badge next to the name (e.g. Verified)',
    testimonialPhotoLayout: 'Author photo',
    testimonialPhotoLayoutLeft: 'Left, full height',
    testimonialPhotoLayoutTop: 'On top',
    testimonialPhotoLayoutAvatar: 'Round avatar',
    testimonialCardBackground: 'Card fill',
    testimonialQuoteMarks: 'Quote marks around the text',
    testimonialQuoteMarksOn: 'Add',
    testimonialQuoteMarksOff: 'None',
    testimonialItalic: 'Text style',
    testimonialItalicOn: 'Italic',
    testimonialItalicOff: 'Upright',
    testimonialBadgeColor: 'Badge color',

    // ── Рейтинг как соц-доказательство ────────────────────────────────────
    ratingValue: 'Rating (0–5)',
    // WEB-3436: пустое поле — не 4.5 по умолчанию, а скрытый блок.
    reviewRatingEmptyHint:
      'Leave empty to hide the block from buyers — no rating is made up.',
    reviewsCount: 'Number of reviews',
    reviewsCaption: 'The “Based on N reviews” caption',
    reviewsCaptionPlaceholder: 'Based on {count} reviews',
    reviewsCaptionHint:
      'Works when the “Number of reviews” field above is filled in.',
    ratingNumberColor: 'Rating number color',
    ratingNumberSize: 'Rating number size, px',
    ratingCaptionColor: 'Color of the caption under the stars',
    ratingCaptionSize: 'Size of the caption under the stars, px',
    ratingStarsRowHint:
      'The star row is not configurable: it was never requested, and the project keeps one star color for every rating block.',

    // ── Значки доверия и плашка-подсказка ─────────────────────────────────
    trustBadges: 'Badges',
    trustBadgeSize: 'Badge size (px)',
    sslBadge: 'SSL secured',
    alertIconEmoji: 'Icon (emoji)',
    alertInfoBlue: 'Info (blue)',
    alertWarningYellow: 'Warning (yellow)',
    alertSuccessGreen: 'Success (green)',

    // ── Пара «до/после» ───────────────────────────────────────────────────
    beforeAfterRolesHint:
      'One setting for both captions. The grey text where an image has not been uploaded is the editor’s own placeholder and does not change.',
    beforeCaption: 'The “before” caption',
    afterCaption: 'The “after” caption',
    beforeImageUrl: '“Before” image URL',
    afterImageUrl: '“After” image URL',
    regenerateBefore: 'Regenerate “before”',
    regenerateAfter: 'Regenerate “after”',
    beforeAfterCaptionColor: 'Color of the “before” and “after” captions',
    beforeAfterCaptionSize: 'Size of the “before” and “after” captions, px',

    // ── Галерея ───────────────────────────────────────────────────────────
    galleryImages: 'Images',
    galleryAddImage: 'Add image',

    // ── Обратный отсчёт ───────────────────────────────────────────────────
    countdownDate: 'Countdown date',
    countdownModeLabel: 'Countdown end',
    countdownModeMinutes: 'Duration',
    countdownModeDate: 'Exact date',
    countdownMinutes: 'Duration, minutes',
    countdownMinutesHint:
      'The timer starts when the visitor opens the screen. An exact date, if set, wins.',
    countdownSeparator: 'Digit separator',
    separatorNone: 'No separator',
    separatorColon: 'Colon ( : )',
    separatorDot: 'Dot ( . )',
    separatorPipe: 'Pipe ( | )',
    countdownDigitsColor: 'Digit color',
    countdownDigitsSize: 'Digit size (px)',
    countdownDigitsFont: 'Digit font',
    funnelFontDefault: 'Funnel font (default)',
    countdownLabelsColor: 'Color of the labels under the digits',
    countdownLabelsSize: 'Size of the labels under the digits, px',
    countdownRolesHint:
      'Colors and resizes the “days/hours/minutes/seconds” labels. The digits have their own settings above. Empty — as now: 12px in the muted theme color.',

    // ── Значок со скидкой ─────────────────────────────────────────────────
    badgeShape: 'Badge shape',
    badgeOval: 'Oval (as before)',
    badgeRibbon: 'Ribbon with notches',
    badgeRect: 'Rectangular',
    badgeNoFrame: 'No border or background',
    badgeFilled: 'Filled background',
    badgeButtonPlaceholder: 'Claim now',
    badgeButtonUrl: 'Button URL (optional)',
    badgeButtonPlacement: 'Button placement',
    placementInline: 'Inline',
    placementAbove: 'Above',
    placementBelow: 'Below',
    badgeButtonTextColor: 'Button text color',
    badgeButtonTextAuto: 'Auto from the button fill',

    // ── Скретч-карта ──────────────────────────────────────────────────────
    scratchType: 'Type',
    scratchCoverLabel: 'Text on the cover',
    scratchCoverPlaceholder: 'Scratch to reveal',
    scratchCoverBg: 'Cover background',
    scratchSolidColor: 'Solid color',
    scratchOwnImage: 'Custom image',
    scratchTexturePlaceholder: 'https://…/texture.jpg (or upload a file)',
    scratchDiscount: 'Discount under the cover',
    scratchDiscountPlaceholder: 'Well done!',
    scratchDiscountCaption: 'Caption under the discount',
    scratchDiscountCaptionPlaceholder: 'Continue to the next step',
    scratchFallbackButton: 'Fallback button label',
    scratchFallbackButtonPlaceholder: 'Reveal',
    scratchLayerColor: 'Cover color',
    scratchStylePackAccent: 'Style pack accent',
    scratchThreshold: 'Scratch to reveal, %',
    scratchHint:
      'Scratching is not the only way: there is always a “Reveal” button under the card, and the text under the cover is available to a screen reader right away. Revealing changes nothing at checkout: write only what the prices already give.',

    // ── Размытый тизер ────────────────────────────────────────────────────
    blurHint:
      'The blur is decoration, not protection: the text under it sits in the page source. Put a promise here, not the paid result.',
    blurCards: 'Cards',
    blurAddCard: 'Add card',
    blurStrength: 'Blur strength, px',
    blurVisibleCaption: 'Caption (visible)',
    blurTeaserPlaceholder: 'Teaser (blurred)',
    emojiPlaceholder: 'Emoji',
    customIconUrl: 'Custom icon (URL); empty = emoji',
    blurLayout: 'Layout',
    layoutGrid: 'Grid',
    layoutList: 'List',
    blurConditionCaption: 'Unlock caption',
    blurConditionPlaceholder: 'Unlocks after payment',
    blurConditionColor: 'Unlock caption color',
    blurConditionSize: 'Unlock caption size, px',
    blurLockIcon: 'Lock icon',
    blurLockIconPlaceholder: 'URL; empty = the standard lock',
    blurRolesHint:
      'The size is set by the row title — the teaser under it and the “hidden until purchase” caption follow it in the same proportion. The unlock caption under the cards has its own fields above.',

    // ── Плашки-фичи ───────────────────────────────────────────────────────
    featureChips: 'Chips',
    featureChipAdd: 'Add chip',
    featureChipLabel: 'Chip label',
    featureChipEmoji: 'Emoji icon (optional)',
    featureChipBg: 'Chip background',
    featureChipTextSize: 'Text size, px',
    featureChipsPerRow: 'Chips per row',
    featureChipsBgDensity: 'Chip background density',
    featureChipsTitleColor: 'Color of the heading above the chips',
    featureChipsTitleSize: 'Size of the heading above the chips, px',
    featureChipsTextColor: 'Chip text color',
    featureChipsTextSize: 'Chip text size, px',

    // ── График-прогноз ────────────────────────────────────────────────────
    forecastNowCaption: 'Caption of the “now” point',
    forecastNowValue: 'Value at the “now” point',
    forecastGoalBalloon: 'Text in the goal balloon',
    forecastAxisLeft: 'Left axis label',
    forecastAxisRight: 'Right axis label',
    forecastCaption: 'Caption under the chart',
    forecastCurve: 'Curve shape',
    curveAccelerate: 'Accelerating to the goal',
    curveLinear: 'Steady growth',
    curveFastStart: 'Fast start',
    nowPlaceholder: 'Now',
    books2Placeholder: '2 books a month',
    books20Placeholder: '20 books a month',
    todayLowerPlaceholder: 'today',
    inThreeMonthsPlaceholder: 'in 3 months',
    forecastCaptionPlaceholder: 'You word the forecast terms yourself',
    forecastFutureColor: 'Color of the “future”',

    // ── Полосы «сейчас/цель» ──────────────────────────────────────────────
    nowGoalNowColumn: 'Caption of the “now” column',
    nowGoalGoalColumn: 'Caption of the “goal” column',
    nowGoalRows: 'Metrics',
    nowGoalAddRow: 'Add metric',
    nowGoalRowName: 'Metric name',
    // WEB-3478: подпись новой или очищенной строки — схема сервера не
    // принимает пустую, пустая валила сохранение всей воронки (400).
    // WEB-3487: выбирается по языку ВОРОНКИ, не кабинета.
    nowGoalRowDefaultLabel: 'Metric',
    nowGoalNowValue: 'The “now” value',
    nowGoalNowBar: 'Length of the “now” bar, %',
    nowGoalGoalValue: 'The “goal” value',
    nowGoalGoalBar: 'Length of the “goal” bar, %',
    nowGoalCardCaption: 'Caption under the card',
    goalPlaceholder: 'Goal',
    nowGoalAccentColor: 'Goal bar colour',
    nowGoalRolesHint:
      'One setting for both sides: the “now” column stays muted by itself — otherwise the contrast the block is drawn for would disappear. The metric name follows the captions in the same proportion.',
    nowGoalTitleColor: 'Block heading color',
    nowGoalTitleSize: 'Block heading size, px',
    nowGoalBarsColor: 'Bar caption color',
    nowGoalBarsSize: 'Bar caption size, px',

    // ── Шкала ИМТ ─────────────────────────────────────────────────────────
    bmiValue: 'Value on the scale',
    bmiValuePlaceholder: '22.5 or {{answer.blockId}}',
    bmiCaptionAbove: 'Caption above the value',
    bmiCaptionPlaceholder: 'Your BMI',
    // Решение владельца №5 (повтор Slimkit): источник числа шкалы.
    bmiSourceLabel: 'Where the value comes from',
    bmiSourceManual: 'I enter it myself',
    bmiSourceComputed: 'Calculate from height and weight',
    // Решение владельца №5: роль «Выбора веса» для вычисляемых значений.
    weightRoleLabel: 'Whose weight is this',
    weightRoleCurrent: 'Current weight',
    weightRoleGoal: 'Goal weight',
    bmiScaleStart: 'Scale start',
    bmiScaleEnd: 'Scale end',
    bmiZones: 'Scale zones',
    bmiAddZone: 'Add zone',
    bmiZoneLabel: 'Zone label',
    // WEB-3487: подпись новой или очищенной зоны — схема сервера не принимает
    // пустую (400 на всю воронку). Выбирается по языку ВОРОНКИ, не кабинета.
    bmiZoneDefaultLabel: 'Zone',
    bmiZoneUpper: 'Zone upper bound',
    bmiDisclaimer: 'Disclaimer under the scale',
    bmiDisclaimerPlaceholder: 'A guideline, not a medical opinion',
    bmiRolesHint:
      'The label of the zone the visitor falls into stays bright while the neighbours stay muted — that is how the result reads. Set the color for the active zone.',
    bmiNumberColor: 'BMI number color',
    bmiNumberSize: 'BMI number size, px',
    bmiNumberHint:
      'The caption above the number follows it: the same color muted, and the same size proportion as before.',
    bmiZoneTextColor: 'Color of the text under the labels',
    bmiZoneTextSize: 'Size of the text under the labels, px',
    bmiTitleColor: 'Scale heading color',
    bmiTitleSize: 'Scale heading size, px',
    bmiZoneLabelColor: 'Zone label color',
    bmiZoneLabelSize: 'Zone label size, px',

    // ── Утверждение с реакциями ───────────────────────────────────────────
    statementQuestionRow: 'Question line under the statement',
    statementQuestionPlaceholder: 'How much is this about you?',
    statementReactionNo: 'Label of the ✕ reaction',
    statementReactionMaybe: 'Label of the 🤔 reaction',
    statementReactionYes: 'Label of the ✓ reaction',
    statementShowMiddle: 'Show the middle 🤔 reaction',
    statementNoPlaceholder: 'Not me',
    statementMaybePlaceholder: 'Sometimes',
    statementYesPlaceholder: 'That’s me',
    statementRolesHint:
      '“Captions under the statement” means the question line and the three emoji captions at once. The color you set is applied in full to the chosen reaction, the others are muted from the same color — otherwise the choice would not be visible.',
    statementValuesHint:
      'The answer is stored as disagree / unsure / agree — use exactly these values in branching conditions. The button labels do not affect them.',
    statementColor: 'Statement color',
    statementSize: 'Statement size, px',
    statementCaptionsColor: 'Color of the captions under the statement',
    statementCaptionsSize: 'Size of the captions under the statement, px',

    // ── Карточка предпочтения ─────────────────────────────────────────────
    preferenceImage: 'Image (URL)',
    preferenceEmoji: 'Emoji instead of an image',
    preferenceCaption: 'Caption under the image',
    preferenceCaptionPlaceholder: 'Book or offer title',
    preferenceNo: 'Label of the ✕ answer',
    preferenceYes: 'Label of the ✓ answer',
    preferenceNoPlaceholder: 'Not for me',
    preferenceYesPlaceholder: 'Interesting',
    preferenceRolesHint:
      '“Captions under the question” means the caption under the image and both answer labels at once. The setting does not touch the emoji or the ✕ / ✓ glyphs.',
    preferenceValuesHint:
      'The answer is stored as no / yes — use exactly these values in branching conditions.',
    preferenceQuestionColor: 'Question color',
    preferenceQuestionSize: 'Question size, px',
    preferenceCaptionsColor: 'Color of the captions under the question',
    preferenceCaptionsSize: 'Size of the captions under the question, px',

    // ── Промо-билет ───────────────────────────────────────────────────────
    ticketTitle: 'Ticket heading',
    ticketCode: 'Code',
    ticketCodePlaceholder: 'Your promo code',
    ticketCodeCaption: 'Caption under the code',
    ticketCodeCaptionPlaceholder: 'You choose the wording',
    ticketOldPrice: 'Old price',
    ticketNewPrice: 'New price',
    ticketPriceHint:
      'This is presentation only — the amount actually charged is decided by Stripe.',
    ticketShowDeadline: 'Show the deadline',
    ticketTimerAria: 'Timer label (for screen readers)',
    ticketTimerAriaPlaceholder: 'Time left on this offer',
    ticketMinutes: 'Deadline, minutes',
    ticketExactDate: 'Exact end date',
    ticketExactDatePlaceholder:
      '2026-08-01T12:00:00Z (takes priority over minutes)',
    ticketRolesHint:
      'The ticket heading and its border share one money accent — that is what makes the ticket look whole. A custom heading color breaks that link; empty — as now.',
    ticketCodeColor: 'Code color',
    ticketCodeSize: 'Code size, px',
    ticketTimerColor: 'Ticket timer color',
    ticketTimerSize: 'Ticket timer size, px',
    ticketTitleColor: 'Ticket heading color',
    ticketTitleSize: 'Ticket heading size, px',
    ticketCodeCaptionColor: 'Color of the caption under the code',
    ticketCodeCaptionSize: 'Size of the caption under the code, px',
    ticketMonoHint:
      'The monospace font of the code and the timer does not change: that is how they are recognized. The ticket prices are not colored by these settings — they work as a “was/now” pair.',

    // ── Индикатор шага ────────────────────────────────────────────────────
    progressTextHint:
      'The block prints a line like “Step 2 of 8” — the number of the screen the visitor is on. The number comes from the actual position in the funnel, so after jumps made by logic rules it may differ from the numbers below: those are only a fallback for when the position is unknown (on a single-screen preview, for example). While the block is on the screen, the funnel does not draw its own automatic progress indicator in the header — so that there are never two counters.',
    progressCurrentStep: 'Current step',
    progressTotalSteps: 'Total steps',
    progressStepWord: 'The word “step”',

    // ── Ветвление ─────────────────────────────────────────────────────────
    branchLogicTitle: 'Transitions are configured in the “Logic” window',
    branchLogicText:
      'Where the buyer goes after the screen is decided by the funnel rules: a condition, and a jump to a screen, to the finish or to an external address. The block itself has no settings.',
    branchOpenLogic: 'Open the “Logic” window',
    branchLegacyTitle: 'The block’s old settings are inactive',
    branchLegacyText:
      'These values are still stored in the funnel, but no transition runs on them. Move them into the rules of the “Logic” window.',
    branchVariableName: 'Variable name',
    branchExpectedValue: 'Expected value',
    branchNextScreenId: 'Next screen ID',

    // ── Выбор картинкой, вес и рост ───────────────────────────────────────
    imageChoiceQuestionPlaceholder: 'Enter the question text...',
    imageChoiceOptions: 'Options (image + caption)',
    imageChoiceOptionLabel: 'Option caption',
    multipleChoice: 'Multiple choice',
    weightQuestionPlaceholder: 'What is your current weight?',
    heightQuestionPlaceholder: 'How tall are you?',
    defaultUnit: 'Default unit',
    unitKg: 'kg',
    unitLb: 'lb',
    unitCm: 'cm',
    unitFt: 'ft',
    // stylepack/unit-picker-look: вид значения и переключателя единиц.
    unitPickerLookTitle: 'Value look',
    unitPickerLookField: 'Field',
    unitPickerLookBigNumber: 'Big number',
    unitPickerValueFontSize: 'Digit size',
    unitPickerUnderline: 'Line under the digits',
    unitToggleTitle: 'Unit switch',
    unitToggleLookButtons: 'Buttons',
    unitToggleLookPill: 'Pill',
    unitTogglePosition: 'Switch position',
    unitTogglePositionRight: 'Right',
    unitTogglePositionAbove: 'Above',
    unitTogglePositionBelow: 'Below',
    unitToggleTextTransform: 'Unit label case',
    unitToggleTextTransformNone: 'As is',
    unitToggleTextTransformUppercase: 'Uppercase',
    unitToggleFontSize: 'Unit label size',
    unitPickerValueColor: 'Digit colour',
    unitPickerUnderlineColor: 'Line colour',
    unitToggleActiveBg: 'Chosen unit fill',
    unitToggleActiveText: 'Chosen unit text',
    unitToggleBg: 'Switch track',
    unitToggleText: 'Other unit text',
    // Задание stylepack/unit-set: набор единиц — обе или одна.
    unitOptionsTitle: 'Units',
    unitOptionsBoth: 'Both',
    unitOptionsKg: 'Kg only',
    unitOptionsLb: 'Lb only',
    unitOptionsCm: 'Cm only',
    unitOptionsFt: 'Ft only',

    // ── Экран загрузки ────────────────────────────────────────────────────
    loaderTitleLabel: 'Heading',
    loaderTitlePlaceholder: 'Analyzing your answers...',
    loaderSkipLabel: 'The “Skip” button in the modal',
    loaderSkipPlaceholder: 'Empty — in the funnel language',
    loaderPopupBg: 'Question popup background',
    loaderPopupText: 'Popup text color',
    loaderPopupOptions: 'Option fill in the popup',
    loaderSteps: 'Loading steps',
    loaderStepsEmpty:
      'While there are no steps, the stages are named after the funnel sections — the same screen names the buyer saw on the way here. Add a step to set your own.',
    loaderAddStep: 'Add step',
    loaderMs: 'ms',
    loaderQuotesTitle: 'Testimonial carousel',
    loaderQuotesHint:
      'While the screen is loading, the lines below are shown one at a time, replacing each other on a timer. This does not make the loading screen any longer.',
    loaderQuotesEmpty:
      'There are no lines of your own, and no “Testimonials” blocks in the funnel either — the carousel has nothing to show. Add a line here, or a “Testimonials” block to any screen.',
    loaderAddQuote: 'Add line',
    loaderQuestionsTitle: 'Questions during loading',
    loaderQuestionsHint:
      'A question is shown after the stage of the same number (the first one — after the first stage). A question tied to the last stage is not shown, so as not to delay the transition. The funnel style pack applies to it.',
    loaderAddQuestion: 'Add question',
    loaderAutoAdvance: 'Auto-advance after loading',
    // WEB-1692 unit 8: the heading of the waiting screen separately from
    // everything that serves it — stage names, answer cards and the review
    // line. The popup question and the percentage are out.
    loaderTitleColor: 'Heading colour',
    loaderTitleSize: 'Heading size, px',
    loaderCaptionsColor: 'Colour of stages, answer cards and reviews',
    loaderCaptionsSize: 'Size of stages, answer cards and reviews, px',
    loaderTextRolesHint:
      'One setting covers the stage names, the cards with the answers and the review line. Stages still to come are dimmed by your own colour, so the order of the waiting screen is still readable. The question popup and the percentage keep their own settings.',

    // ── График цели ───────────────────────────────────────────────────────
    chartTitleOptional: 'Heading (optional)',
    chartType: 'Chart type',
    chartProgressToGoal: 'Progress to the goal',
    chartBeforeAfter: 'Comparison (before/after)',
    chartStartLabel: 'Start label',
    chartGoalLabel: 'Goal label',
    chartStartValue: 'Start value',
    chartGoalValue: 'Goal value',
    chartProgressPlaceholder: 'Your progress',
    chartTodayPlaceholder: 'Today',
    chartWeightNowPlaceholder: 'e.g. 92 kg',
    chartWeightGoalPlaceholder: 'e.g. 78 kg',
    chartAccentColor: 'Accent color',
    // WEB-3252: the «Bars» kind — 2..5 captioned bars, one or more highlighted.
    chartKindBars: 'Bars',
    chartBarsList: 'Bars (from 2 to 5)',
    chartBarLabel: 'Caption under the bar',
    chartBarValue: 'Bar height, % (0–100)',
    chartBarValueText: 'Text above the bar',
    chartBarValueTextPlaceholder: 'e.g. x2.2',
    chartBarHighlight: 'Highlight with the accent colour',
    chartBarAdd: 'Add bar',
    // WEB-1692 unit 8: the heading of the chart separately from the captions of
    // the scale. The goal value stays on the accent colour — it is the point of
    // the drawing.
    chartHeadingColor: 'Heading colour',
    chartHeadingSize: 'Heading size, px',
    chartScaleLabelsColor: 'Colour of the scale captions and the start value',
    chartScaleLabelsSize: 'Size of the scale captions and the start value, px',
    chartTextRolesHint:
      'In the comparison chart the captions are drawn inside the picture, and the picture is stretched to the width of the screen: the number you set there works as a proportion, not as exact pixels on the phone. The goal value keeps the accent colour.',

    // ── Согласие на cookie ────────────────────────────────────────────────
    cookieMessage: 'Message text',
    cookieMessagePlaceholder: 'We use cookies to improve your experience.',
    cookieAcceptText: 'Text of the “Accept” button',
    cookieDeclineText: 'Text of the “Decline” button',
    cookieAcceptPlaceholder: 'Accept',
    cookieDeclinePlaceholder: 'Decline',
    cookiePolicyUrl: 'Cookie policy URL (optional)',
    cookiePosition: 'Position',
    cookieBottom: 'At the bottom of the page',
    cookieModal: 'Modal window',
    // WEB-1692 unit 7: two semantic texts of the banner — the notice itself and
    // the labels of both buttons.
    cookieTextColor: 'Notice text color',
    cookieTextSize: 'Notice text size, px',
    cookieButtonsTextColor: 'Color of both button labels',
    cookieButtonsTextSize: 'Size of both button labels, px',
    cookieTextRolesHint:
      'Both buttons take one setting — they are a pair of the same action. The “Learn more” link follows the notice size but keeps its own accent color, so it still reads as a link. Button fill is set by the element surface fields.',

    // ── Загрузка фото ─────────────────────────────────────────────────────
    photoQuestionPlaceholder: 'Upload a photo',
    photoMaxSize: 'Max file size (MB)',
    photoAcceptTypes: 'Allowed file types',
    photoAcceptPlaceholder: 'Choose file types',
    photoAnyImage: 'Any images',

    // ── Условия оплаты ────────────────────────────────────────────────────
    paymentTermsPlaceholder:
      'You will be charged {{price}} monthly. Cancel any time.',
    paymentTermsPriceTitle: 'Where the price in the terms text comes from',
    paymentTermsPriceText:
      'The terms text must promise the amount the pay button will actually charge. That is why the price is looked up in order: a live plan choice (on this screen or on any screen of the funnel) → the own price of this screen’s main pay button (the main one is the button with “Always show”, otherwise the last one; the sticky bar duplicates it) → the first block with a price on the screen → and only then the field below. The field below is used when there is nowhere else to take the price from.',
    paymentTermsStepPlanSelector:
      'Right now the price comes from the “Plan selector”: the buyer picks it, and the field below is not used.',
    paymentTermsStepPayButton:
      'Right now the price comes from this screen’s main pay button — its own price. The main one is the button with “Always show” in “Sticky button at the top of the screen”, and if there is none — the last pay button on the screen. The sticky bar duplicates it, so the text and the charge match. The field below is not used.',
    paymentTermsStepPricedBlock:
      'Right now the price comes from the first block with a price on this screen. The field below is not used.',
    paymentTermsStepOwnField:
      'Right now the price is taken from the field below: there is no other price source on the screen.',

    // ── Цена по частям ────────────────────────────────────────────────────
    priceBreakdownTitle: 'Where the price comes from',
    pbBeforePrice: 'Text before the selected price',
    pbTotalPlaceholder: 'Just',
    pbEmptyStartsSentence: 'Empty — the price starts the sentence.',
    pbBeforePriceColor: 'Color of the text before the price',
    pbBeforePriceSize: 'Size of the text before the price, px',
    pbAfterPrice: 'Text after the selected price',
    pbPerYearPlaceholder: 'a year',
    pbAfterPriceColor: 'Color of the text after the price',
    pbAfterPriceSize: 'Size of the text after the price, px',
    pbBeforeDaily: 'Text before the daily price',
    pbThatIsPlaceholder: 'That is',
    pbBeforeDailyColor: 'Color of the text before the daily price',
    pbBeforeDailySize: 'Size of the text before the daily price, px',
    pbAfterDaily: 'Text after the daily price',
    pbPerDayPlaceholder: 'a day',
    pbOneTimeHint:
      'A one-off payment has no cycle — the “a day” line is not shown.',
    pbAfterDailyColor: 'Color of the text after the daily price',
    pbAfterDailySize: 'Size of the text after the daily price, px',

    // ── Даунселл-попап ────────────────────────────────────────────────────
    downsellTitle: 'Offer heading',
    downsellTitlePlaceholder: 'Wait — here is a special offer',
    downsellText: 'Offer text',
    downsellTextPlaceholder: 'Right now only — a discount on the first month',
    downsellButtonPlaceholder: 'Get the discount',
    downsellStripePrice: 'Discounted Stripe price',
    downsellShowWhen: 'Show on',
    downsellTriggerDeclined: 'Payment declined',
    downsellTriggerExit: 'Attempt to leave the page',
    downsellAlwaysPlaceholder: 'Not selected — a card on the screen',
    downsellRolesHint:
      'The price and the button label are set separately below. Empty — as now: heading 18, text 14.',
    downsellPriceColor: 'Discounted price color',
    downsellPriceSize: 'Discounted price size, px',
    downsellTitleColor: 'Offer heading color',
    downsellTitleSize: 'Offer heading size, px',
    downsellTextColor: 'Offer text color',
    downsellTextSize: 'Offer text size, px',

    // ── Список подписок и кнопки управления ───────────────────────────────
    subsListTitle: 'List heading',
    subsEmptyText: 'Text when there are no subscriptions',
    subsInnerFill: 'Fill inside the card',
    subsEmptyColor: 'Color of the “no subscriptions” text',
    subsEmptySize: 'Size of the “no subscriptions” text, px',
    subsRolesHint:
      'The text inside the card is the plan line with its status and the renewal line under it. Empty — as now.',
    subsListTitleColor: 'List heading color',
    subsListTitleSize: 'List heading size, px',
    subsInnerTextColor: 'Color of the text inside the card',
    subsInnerTextSize: 'Size of the text inside the card, px',
    cancelButtonText: 'Cancel button text',
    resumeButtonText: 'Resume button text',

    // ── Оффер удержания ───────────────────────────────────────────────────
    retentionShowOnCanvas: 'Show on the canvas',
    retentionPreviewOffer: 'Offer',
    retentionPreviewAccepted: 'After acceptance',
    retentionPreviewNoAccess: 'No access',
    retentionPreviewEmpty: 'Empty',
    retentionTitle: 'Offer heading',
    retentionText: 'Offer text',
    retentionSuccessText: 'Text after success',
    retentionStripePrice: 'Discounted Stripe price (swap target)',
    retentionShowForPrices: 'Show for these original prices',
    retentionAllPricesPlaceholder: 'All prices (no condition)',
    retentionRolesHint:
      'The body is the offer text, the new price line and the text shown after the discount is accepted. Empty — as now.',
    retentionTitleColor: 'Offer heading color',
    retentionTitleSize: 'Offer heading size, px',
    retentionBodyColor: 'Body color',
    retentionBodySize: 'Body size, px',
    retentionButtonFill: 'Fill inside the button',
    retentionInnerFill: 'Inner card fill',

    // ── Подстановки: нумерованные поля списков ──────────────────────────
    listItemPlaceholder: (n: number) => `Item ${n}`,
    optionPlaceholder: (n: number) => `Option ${n}`,
    loaderStepPlaceholder: (n: number) => `Step ${n}`,
    loaderQuotePlaceholder: (n: number) => `Line ${n}`,
    loaderQuoteDeleteAria: (n: number) => `Delete line ${n}`,
    loaderQuestionPlaceholder: (n: number) => `Question ${n}`,
    loaderQuestionDeleteAria: (n: number) => `Delete question ${n}`,
    loaderOptionDeleteAria: (option: number, question: number) =>
      `Delete option ${option} of question ${question}`,
    loaderQuotesFromFunnel: (found: number) =>
      `You have no lines of your own — the carousel will take testimonials from the “Testimonials” blocks of this funnel (${found} found so far). Add a line to set your own texts.`,

    // ── Цена по частям: откуда берётся цена и что делает {{period}} ──────
    priceBreakdownText:
      'The block shows the price of the plan the buyer chose in the funnel (the same plan the pay button will charge), and the same price per day: a year ÷ 365, a month ÷ 30.4, a week ÷ 7. The block has no price of its own. While there is no price to take, the block shows nothing. Do not type the period word by hand: type {{period}} and the period of the CHOSEN plan will take its place (“a month”, “a year”, “every 2 weeks”) in the funnel language — or in English, if that language is outside the set we translate into, so the caption never disagrees with the amount. A one-off payment has no period — the placeholder simply disappears. You can still write the word by hand, but then, if the plans have different periods, word it neutrally — otherwise “a year” will keep hanging over a monthly price.',
    pbPeriodHint:
      '{{period}} — the period of the selected plan (“a month”, “a year”, “every 2 weeks”). The word comes out in the funnel language — the same set of languages you can order a translation for. Works in any of the four fields.',
    pbPeriodHintNoLocale: (language: string) =>
      `We have no period dictionary for “${language}”: the English word (“a month”) will be used instead. Write the period out yourself if that does not fit.`,
    nowGoalHint:
      'You set the values and the bar lengths yourself. To show the visitor’s answer, put a variable like this into the text',
    bmiHintBefore:
      'Enter the value yourself, switch “Where the value comes from” to calculation, or put a variable like',
    bmiHintAfter:
      'The zone labels are yours: this is a guideline, not a medical opinion.',

    // ── Потолки редактируемых списков (обе панели, WEB-1532/1687/1829) ───
    maxBlockOptionsHint: (max: number) =>
      `Maximum reached — ${max} options in one block. Delete one to add another.`,
    maxLoaderStepsHint: (max: number) =>
      `Maximum reached — ${max} loading steps. Delete one to add another.`,
    maxLoaderQuestionsHint: (max: number) =>
      `Maximum reached — ${max} questions on the loading screen. Delete one to add another.`,
    maxLoaderSocialProofHint: (max: number) =>
      `Maximum reached — ${max} testimonial lines. Delete one to add another.`,
    maxPlanSelectorPlansHint: (max: number) =>
      `Maximum reached — ${max} plans in one block. Delete one to add another.`,
    maxFeatureChipsHint: (max: number) =>
      `Maximum reached — ${max} chips in one block. Delete one to add another.`,

    // ── Условия оплаты: поля, найденные сторожем ────────────────────────
    paymentTermsText: 'Terms text',
    paymentTermsPlaceholdersHint: 'Available placeholders:',
    stripePriceLabel: 'Stripe price',

    // ── WEB-2451: панель говорит автору, что увидит посетитель ─────────────
    videoUrlUnrecognized:
      'Link not recognised: the visitor will see an empty block.',
    videoAutoplayMutedOnShowOnly:
      '“Silent autoplay” works only with the start “On display”.',
    videoAutoplayMutedClickConflict:
      'With the start “On click” this option plays without sound — choose “With sound” or “Muted”.',
    countdownExpiryDateHint:
      'When the countdown reaches zero, the block shows zeros, while the screen and the offer stay. Remove the offer yourself when the promotion ends.',
    countdownExpiryMinutesHint:
      'Without a date the countdown runs from the visitor’s arrival and also stops at zeros; the screen and the offer stay.',
    stockBadgeTextHint:
      'This is a caption: the stock is not counted and does not change by itself.',
    scratchDiscountTextHint:
      'This is only text: the checkout price does not change because of it.',
    downsellNoTriggersWarning:
      'Without conditions the block does not pop up: it stands as a card right on the screen, and every visitor sees the discounted price.',
    trustBadgesRetiredNote:
      'The PayPal badge is no longer shown to buyers: PayPal is not available at checkout. Other badges are shown as before.',
    paymentBadgesOverlapWarning: (found: string) =>
      `This screen also has the other payment badges block with ${found}: the visitor will see these badges twice.`,
  },
  {
    // ── Общая лексика панели (встречается у многих блоков) ────────────────
    color: 'Цвет',
    textColor: 'Цвет текста',
    fillColor: 'Цвет заливки',
    borderColor: 'Цвет рамки',
    itemFill: 'Заливка элемента',
    themeDefault: 'Как в теме',
    sameAsBlock: 'как у блока',
    auto: 'Авто',
    emptyFromPalette: 'Пусто = из палитры',
    emptyThemeAccent: 'Пусто = акцент темы',
    emptyNoFill: 'Пусто = без заливки',
    emptyWhite: 'Пусто = белый',
    emptyFunnelAccent: 'Пусто = акцент воронки',
    emptyEmojiFallback: 'Пусто = эмодзи-заглушка',
    delete: 'Удалить',
    show: 'Показать',
    hide: 'Скрыть',
    alignLeft: 'Влево',
    alignCenter: 'По центру',
    alignRight: 'Вправо',
    alignment: 'Выравнивание',
    required: 'Обязательное',
    minValue: 'Мин. значение',
    maxValue: 'Макс. значение',
    placeholderLabel: 'Плейсхолдер',
    question: 'Вопрос',
    answer: 'Ответ',
    name: 'Название',
    heightPx: 'Высота (px)',
    spacerMode: 'Режим',
    spacerModeFixed: 'Фиксированный',
    spacerModeFlexible: 'Заполнить свободное место',
    spacerMinHeightPx: 'Минимальная высота (px)',
    spacerFlexibleHint:
      'Забирает всё свободное место экрана и прижимает нижние блоки к низу. Когда места нет, скролла не добавляет. Выравнивание экрана по вертикали не действует.',
    widthPx: 'Ширина (px)',
    step: 'Шаг',
    buttonText: 'Текст кнопки',
    imageUrl: 'URL изображения',
    regenerate: 'Перегенерировать',
    autoAdvance: 'Переходить дальше сразу после выбора',
    buttonLabelColor: 'Цвет надписи на кнопке',
    buttonLabelSize: 'Размер надписи на кнопке, px',

    // ── Разделитель ───────────────────────────────────────────────────────
    dividerLineStyle: 'Стиль линии',
    dividerSolid: 'Сплошная',
    dividerDashed: 'Пунктирная',
    dividerEdges: 'Края',
    edgesSquare: 'Прямые',
    edgesRounded: 'Скруглённые',
    dividerFullWidth: 'во всю ширину',

    // ── Иконка и маркированный список ─────────────────────────────────────
    iconLabel: 'Иконка',
    iconStar: 'Звезда',
    iconHeart: 'Сердце',
    iconCheck: 'Галочка',
    iconInfo: 'Инфо',
    iconWarning: 'Внимание',
    iconFire: 'Огонь',
    iconBolt: 'Молния',
    iconGift: 'Подарок',
    iconLock: 'Замок',
    iconShield: 'Щит',
    iconMagnifier: 'Лупа',
    iconClock: 'Часы',
    iconCalendar: 'Календарь',
    iconMoney: 'Деньги',
    iconChart: 'График',
    iconTrophy: 'Кубок',
    iconRocket: 'Ракета',
    iconBulb: 'Лампочка',
    iconPhone: 'Телефон',
    iconMail: 'Письмо',
    iconClip: 'Скрепка',
    listItems: 'Элементы',
    listAddItem: 'Добавить элемент',

    // ── FAQ (аккордеон) ───────────────────────────────────────────────────
    faqItems: 'Вопросы FAQ',
    faqAddItem: 'Добавить вопрос FAQ',
    faqRolesHint:
      'Настройки действуют на ВСЕ пункты сразу. Пусто — как сейчас: заголовок 16px полужирным, ответ 15px приглушённым цветом темы.',
    faqTitleColor: 'Цвет заголовков пунктов',
    faqTitleSize: 'Размер заголовков пунктов, px',
    faqBodyColor: 'Цвет раскрывающегося текста',
    faqBodySize: 'Размер раскрывающегося текста, px',

    // ── Две колонки ───────────────────────────────────────────────────────
    columnsLeftTitle: 'Заголовок левой колонки',
    columnsLeftContent: 'Контент левой колонки',
    columnsLeftContentPlaceholder: 'Содержимое левой колонки',
    columnsLeftAlign: 'Выравнивание левой колонки',
    columnsRightTitle: 'Заголовок правой колонки',
    columnsRightContent: 'Контент правой колонки',
    columnsRightContentPlaceholder: 'Содержимое правой колонки',
    columnsRightAlign: 'Выравнивание правой колонки',
    columnsRolesHint:
      'Настройка идёт на роль текста, а не на колонку: одна пара значений красит заголовки ОБЕИХ колонок, вторая — содержимое обеих. Пусто — как сейчас.',
    columnsTitleColor: 'Цвет заголовков колонок',
    columnsTitleSize: 'Размер заголовков колонок, px',
    columnsBodyColor: 'Цвет содержимого колонок',
    columnsBodySize: 'Размер содержимого колонок, px',

    // ── Карточка ──────────────────────────────────────────────────────────
    cardTitle: 'Заголовок карточки',
    cardDescription: 'Описание',
    cardDescriptionPlaceholder: 'Описание карточки',
    cardImageUrl: 'URL изображения (необязательно)',
    imageChoiceOptionGone:
      'Готовая картинка не применена: этого варианта ответа больше нет.',
    imageChoiceAutoAdvanceSingleOnly:
      'Только для одиночного выбора: при множественном посетитель нажимает «Далее».',
    cardVariantLabel: 'Вид карточки',
    cardVariantSolid: 'Сплошная',
    cardVariantGlass: 'Стекло',
    // WEB-3243: картинка карточки сверху или фоном под текстом.
    cardImageModeLabel: 'Картинка',
    cardImageModeTop: 'сверху',
    cardImageModeBackground: 'фоном',
    cardImageModeGlassHint:
      'При картинке фоном вид карточки («Стекло») не действует: подложкой служит картинка.',
    cardRolesHint:
      'Пусто — как сейчас: заголовок 18px, описание 15px приглушённым цветом темы.',
    cardTitleColor: 'Цвет заголовка карточки',
    cardTitleSize: 'Размер заголовка карточки, px',
    cardDescriptionColor: 'Цвет описания',
    cardDescriptionSize: 'Размер описания, px',

    // ── Поля ввода: дата, число, выпадающий список ────────────────────────
    datePlaceholder: 'Выберите дату',
    numberPlaceholder: 'Введите число',
    selectPlaceholder: 'Выберите...',
    selectOptions: 'Варианты',
    selectAddOption: 'Добавить вариант',

    // ── Оценка звёздами ───────────────────────────────────────────────────
    ratingLabel: 'Подпись',
    ratingLabelPlaceholder: 'Оцените ваш опыт',
    ratingMaxStars: 'Макс. число звёзд',
    ratingStarsAlign: 'Выравнивание звёзд',
    ratingStarColor: 'Цвет звёзд',
    ratingStarSize: 'Размер звёзд, px',
    ratingStarsHint:
      'Незакрашенная звезда возьмёт тот же цвет и останется бледнее — так видно выбранную оценку. Текст подписи настраивается полями «Размер шрифта» и «Цвет текста внутри элемента».',

    // ── Ползунок ──────────────────────────────────────────────────────────
    sliderLabelColor: 'Цвет подписи',
    sliderLabelSize: 'Размер подписи, px',
    sliderNumbersColor: 'Цвет чисел под ползунком',
    sliderNumbersSize: 'Размер чисел под ползунком, px',

    // ── Чекбокс, имя, видео ───────────────────────────────────────────────
    checkboxDefaultState: 'Состояние по умолчанию',
    namePlaceholder: 'Ваше имя',
    videoUrl: 'URL видео (YouTube / Vimeo)',
    videoStart: 'Запуск',
    videoStartOnClick: 'По клику',
    videoStartOnShow: 'При показе',
    videoSound: 'Звук',
    videoWithSound: 'Со звуком',
    videoMuted: 'Без звука',
    videoAutoplayMuted: 'Тихий автозапуск',
    videoSoundHint:
      'При автопоказе экрана браузеры всегда запускают видео без звука — «Со звуком» действует для запуска по клику.',
    videoClickUnmute: 'Клик по видео включает звук',
    videoProgressBar: 'Перемотка (прогрессбар)',
    videoOnShowHint:
      'При запуске «При показе» звук всегда выключен: браузеры разрешают автозапуск видео только без звука. Включить звук зритель сможет сам — или поставьте запуск «По клику».',
    videoUrlHint:
      'Ссылка YouTube (обычная, youtu.be, Shorts, встраивание) или Vimeo либо прямая ссылка на файл видео (.mp4, .webm, .mov, .m4v).',
    videoFileSection: 'Видео файлом',
    videoLoop: 'По кругу',
    videoFit: 'Заполнение',
    videoFitCover: 'Заполнить (с обрезкой)',
    videoFitContain: 'Целиком',
    videoBoxHeight: 'Высота, px',
    videoBoxHeightAuto: 'По ролику',

    // ── Отзывы ────────────────────────────────────────────────────────────
    testimonialsItems: 'Отзывы',
    testimonialsAdd: 'Добавить отзыв',
    testimonialName: 'Имя',
    testimonialText: 'Текст отзыва',
    testimonialRating: 'Оценка (1-5)',
    testimonialsRolesHint:
      'Настройка действует на ВСЕ отзывы блока разом — как и рамка карточки отзыва. Звёзды оценки под неё не попадают.',
    testimonialAuthorColor: 'Цвет имени автора',
    testimonialAuthorSize: 'Размер имени автора, px',
    testimonialTextColor: 'Цвет текста отзыва',
    testimonialTextSize: 'Размер текста отзыва, px',
    // Слово владельца 21.10.2026: фото автора, отметка и вид карточки отзыва.
    testimonialPhotoUrl: 'Адрес фото автора',
    testimonialPhotoUrlInvalid:
      'Такой адрес не принимается: нужна ссылка https:// или путь к файлу.',
    testimonialBadge: 'Отметка у имени (например, «Проверено»)',
    testimonialPhotoLayout: 'Фото автора',
    testimonialPhotoLayoutLeft: 'Слева во всю высоту',
    testimonialPhotoLayoutTop: 'Сверху',
    testimonialPhotoLayoutAvatar: 'Круглый аватар',
    testimonialCardBackground: 'Фон карточки',
    testimonialQuoteMarks: 'Кавычки вокруг текста',
    testimonialQuoteMarksOn: 'Ставить',
    testimonialQuoteMarksOff: 'Не ставить',
    testimonialItalic: 'Начертание текста',
    testimonialItalicOn: 'Курсив',
    testimonialItalicOff: 'Прямое',
    testimonialBadgeColor: 'Цвет отметки',

    // ── Рейтинг как соц-доказательство ────────────────────────────────────
    ratingValue: 'Рейтинг (0–5)',
    reviewRatingEmptyHint:
      'Пустое поле скрывает блок у покупателя — рейтинг не придумывается.',
    reviewsCount: 'Количество отзывов',
    reviewsCaption: 'Подпись «На основе N отзывов»',
    reviewsCaptionPlaceholder: 'На основе {count} отзывов',
    reviewsCaptionHint:
      'Работает при заполненном поле «Количество отзывов» выше.',
    ratingNumberColor: 'Цвет цифры рейтинга',
    ratingNumberSize: 'Размер цифры рейтинга, px',
    ratingCaptionColor: 'Цвет подписи под звёздами',
    ratingCaptionSize: 'Размер подписи под звёздами, px',
    ratingStarsRowHint:
      'Ряд звёзд не настраивается: владелец о нём не просил, а цвет звезды в проекте один на все блоки с рейтингом.',

    // ── Значки доверия и плашка-подсказка ─────────────────────────────────
    trustBadges: 'Значки',
    trustBadgeSize: 'Размер значков (px)',
    sslBadge: 'SSL защита',
    alertIconEmoji: 'Иконка (эмодзи)',
    alertInfoBlue: 'Инфо (синий)',
    alertWarningYellow: 'Предупреждение (жёлтый)',
    alertSuccessGreen: 'Успех (зелёный)',

    // ── Пара «до/после» ───────────────────────────────────────────────────
    beforeAfterRolesHint:
      'Одна настройка на обе подписи. Серый текст на месте незагруженной картинки — служебный вид редактора, он не меняется.',
    beforeCaption: 'Подпись «до»',
    afterCaption: 'Подпись «после»',
    beforeImageUrl: 'URL изображения «до»',
    afterImageUrl: 'URL изображения «после»',
    regenerateBefore: 'Перегенерировать «до»',
    regenerateAfter: 'Перегенерировать «после»',
    beforeAfterCaptionColor: 'Цвет подписей «до» и «после»',
    beforeAfterCaptionSize: 'Размер подписей «до» и «после», px',

    // ── Галерея ───────────────────────────────────────────────────────────
    galleryImages: 'Изображения',
    galleryAddImage: 'Добавить изображение',

    // ── Обратный отсчёт ───────────────────────────────────────────────────
    countdownDate: 'Дата отсчёта',
    countdownModeLabel: 'Чем заканчивается отсчёт',
    countdownModeMinutes: 'Длительность',
    countdownModeDate: 'Конкретная дата',
    countdownMinutes: 'Длительность, минут',
    countdownMinutesHint:
      'Отсчёт начинается, когда посетитель открывает экран. Конкретная дата, если она задана, главнее.',
    countdownSeparator: 'Разделитель цифр',
    separatorNone: 'Без разделителя',
    separatorColon: 'Двоеточие ( : )',
    separatorDot: 'Точка ( . )',
    separatorPipe: 'Черта ( | )',
    countdownDigitsColor: 'Цвет цифр',
    countdownDigitsSize: 'Размер цифр (px)',
    countdownDigitsFont: 'Шрифт цифр',
    funnelFontDefault: 'Шрифт воронки (по умолчанию)',
    countdownLabelsColor: 'Цвет подписей под цифрами',
    countdownLabelsSize: 'Размер подписей под цифрами, px',
    countdownRolesHint:
      'Красит и меняет подписи «дни/часы/минуты/секунды». Цифры идут своими настройками выше. Пусто — как сейчас: 12px приглушённым цветом темы.',

    // ── Значок со скидкой ─────────────────────────────────────────────────
    badgeShape: 'Вид значка',
    badgeOval: 'Овальный (как раньше)',
    badgeRibbon: 'Лента с уголками',
    badgeRect: 'Прямоугольный',
    badgeNoFrame: 'Без рамки и фона',
    badgeFilled: 'Закрашенный фон',
    badgeButtonPlaceholder: 'Забрать сейчас',
    badgeButtonUrl: 'URL кнопки (необязательно)',
    badgeButtonPlacement: 'Расположение кнопки',
    placementInline: 'Рядом',
    placementAbove: 'Сверху',
    placementBelow: 'Снизу',
    badgeButtonTextColor: 'Цвет текста кнопки',
    badgeButtonTextAuto: 'Авто по заливке кнопки',

    // ── Скретч-карта ──────────────────────────────────────────────────────
    scratchType: 'Тип',
    scratchCoverLabel: 'Надпись на слое',
    scratchCoverPlaceholder: 'Сотрите, чтобы открыть',
    scratchCoverBg: 'Фон покрытия',
    scratchSolidColor: 'Сплошной цвет',
    scratchOwnImage: 'Своя картинка',
    scratchTexturePlaceholder: 'https://…/texture.jpg (или загрузите файл)',
    scratchDiscount: 'Скидка под слоем',
    scratchDiscountPlaceholder: 'Отлично!',
    scratchDiscountCaption: 'Подпись под скидкой',
    scratchDiscountCaptionPlaceholder: 'Переходите к следующему шагу',
    scratchFallbackButton: 'Подпись кнопки-обхода',
    scratchFallbackButtonPlaceholder: 'Открыть',
    scratchLayerColor: 'Цвет слоя',
    scratchStylePackAccent: 'Акцент стиль-пака',
    scratchThreshold: 'Стереть для раскрытия, %',
    scratchHint:
      'Стирание — не единственный путь: под картой всегда есть кнопка «Открыть», а текст под слоем доступен скринридеру сразу. Стирание ничего не меняет на оплате: пишите только то, что уже дают цены.',

    // ── Размытый тизер ────────────────────────────────────────────────────
    blurHint:
      'Размытие — оформление, а не защита: текст под ним лежит в коде страницы. Кладите сюда обещание, а не платный результат.',
    blurCards: 'Карточки',
    blurAddCard: 'Добавить карточку',
    blurStrength: 'Сила размытия, px',
    blurVisibleCaption: 'Подпись (видна)',
    blurTeaserPlaceholder: 'Приманка (размыта)',
    emojiPlaceholder: 'Эмодзи',
    customIconUrl: 'Своя иконка (URL); пусто = эмодзи',
    blurLayout: 'Раскладка',
    layoutGrid: 'Сетка',
    layoutList: 'Список',
    blurConditionCaption: 'Подпись-условие',
    blurConditionPlaceholder: 'Откроется после оплаты',
    blurConditionColor: 'Цвет подписи-условия',
    blurConditionSize: 'Размер подписи-условия, px',
    blurLockIcon: 'Иконка замочка',
    blurLockIconPlaceholder: 'URL; пусто = стандартный замочек',
    blurRolesHint:
      'Размер задаётся по названию строки — приманка под ним и подпись «скрыто до оплаты» едут за ним в прежней пропорции. Подпись-условие под плашками правится своими полями выше.',

    // ── Плашки-фичи ───────────────────────────────────────────────────────
    featureChips: 'Плашки',
    featureChipAdd: 'Добавить плашку',
    featureChipLabel: 'Подпись плашки',
    featureChipEmoji: 'Эмодзи-иконка (необязательно)',
    featureChipBg: 'Фон плашки',
    featureChipTextSize: 'Размер текста, px',
    featureChipsPerRow: 'Плашек в ряду',
    featureChipsBgDensity: 'Плотность фона плашек',
    featureChipsTitleColor: 'Цвет заголовка над плашками',
    featureChipsTitleSize: 'Размер заголовка над плашками, px',
    featureChipsTextColor: 'Цвет текста в плашках',
    featureChipsTextSize: 'Размер текста в плашках, px',

    // ── График-прогноз ────────────────────────────────────────────────────
    forecastNowCaption: 'Подпись точки «сейчас»',
    forecastNowValue: 'Значение в точке «сейчас»',
    forecastGoalBalloon: 'Текст в цели-балуне',
    forecastAxisLeft: 'Подпись оси слева',
    forecastAxisRight: 'Подпись оси справа',
    forecastCaption: 'Подпись под графиком',
    forecastCurve: 'Форма кривой',
    curveAccelerate: 'Разгон к цели',
    curveLinear: 'Ровный рост',
    curveFastStart: 'Быстрый старт',
    nowPlaceholder: 'Сейчас',
    books2Placeholder: '2 книги в месяц',
    books20Placeholder: '20 книг в месяц',
    todayLowerPlaceholder: 'сегодня',
    inThreeMonthsPlaceholder: 'через 3 месяца',
    forecastCaptionPlaceholder: 'Условия прогноза формулируете вы',
    forecastFutureColor: 'Цвет «будущего»',

    // ── Полосы «сейчас/цель» ──────────────────────────────────────────────
    nowGoalNowColumn: 'Подпись колонки «сейчас»',
    nowGoalGoalColumn: 'Подпись колонки «цель»',
    nowGoalRows: 'Показатели',
    nowGoalAddRow: 'Добавить показатель',
    nowGoalRowName: 'Название показателя',
    nowGoalRowDefaultLabel: 'Показатель',
    nowGoalNowValue: 'Значение «сейчас»',
    nowGoalNowBar: 'Длина бара «сейчас», %',
    nowGoalGoalValue: 'Значение «цель»',
    nowGoalGoalBar: 'Длина бара «цель», %',
    nowGoalCardCaption: 'Подпись под карточкой',
    goalPlaceholder: 'Цель',
    nowGoalAccentColor: 'Цвет полосы цели',
    nowGoalRolesHint:
      'Одна настройка на обе стороны: колонка «сейчас» останется приглушённой сама — иначе пропадёт разница, ради которой блок так и нарисован. Название показателя едет за подписями в прежней пропорции.',
    nowGoalTitleColor: 'Цвет заголовка блока',
    nowGoalTitleSize: 'Размер заголовка блока, px',
    nowGoalBarsColor: 'Цвет подписей полос',
    nowGoalBarsSize: 'Размер подписей полос, px',

    // ── Шкала ИМТ ─────────────────────────────────────────────────────────
    bmiValue: 'Значение на шкале',
    bmiValuePlaceholder: '22.5 или {{answer.blockId}}',
    bmiCaptionAbove: 'Подпись над значением',
    bmiCaptionPlaceholder: 'Ваш ИМТ',
    bmiSourceLabel: 'Откуда значение',
    bmiSourceManual: 'Вписываю сам',
    bmiSourceComputed: 'Считать по росту и весу',
    weightRoleLabel: 'Чей это вес',
    weightRoleCurrent: 'Текущий вес',
    weightRoleGoal: 'Желаемый вес',
    bmiScaleStart: 'Начало шкалы',
    bmiScaleEnd: 'Конец шкалы',
    bmiZones: 'Зоны шкалы',
    bmiAddZone: 'Добавить зону',
    bmiZoneLabel: 'Подпись зоны',
    bmiZoneDefaultLabel: 'Зона',
    bmiZoneUpper: 'Верхняя граница зоны',
    bmiDisclaimer: 'Дисклеймер под шкалой',
    bmiDisclaimerPlaceholder: 'Ориентир, а не медицинское заключение',
    bmiRolesHint:
      'Метка зоны, в которую попал посетитель, останется яркой, а соседние — приглушёнными: так видно результат. Задавайте цвет для активной зоны.',
    bmiNumberColor: 'Цвет числа ИМТ',
    bmiNumberSize: 'Размер числа ИМТ, px',
    bmiNumberHint:
      'Подпись над числом едет за ним: тот же цвет приглушённым и прежняя пропорция размера.',
    bmiZoneTextColor: 'Цвет текста под метками',
    bmiZoneTextSize: 'Размер текста под метками, px',
    bmiTitleColor: 'Цвет заголовка шкалы',
    bmiTitleSize: 'Размер заголовка шкалы, px',
    bmiZoneLabelColor: 'Цвет меток зон',
    bmiZoneLabelSize: 'Размер меток зон, px',

    // ── Утверждение с реакциями ───────────────────────────────────────────
    statementQuestionRow: 'Строка-вопрос под утверждением',
    statementQuestionPlaceholder: 'Насколько это про вас?',
    statementReactionNo: 'Подпись реакции ✕',
    statementReactionMaybe: 'Подпись реакции 🤔',
    statementReactionYes: 'Подпись реакции ✓',
    statementShowMiddle: 'Показывать среднюю реакцию 🤔',
    statementNoPlaceholder: 'Не про меня',
    statementMaybePlaceholder: 'Иногда',
    statementYesPlaceholder: 'Это я',
    statementRolesHint:
      '«Подписи под утверждением» — это строка-вопрос и три подписи под эмодзи разом. Заданный цвет ставится подписи выбранной реакции целиком, невыбранные приглушаются от него же — иначе не было бы видно, что выбрано.',
    statementValuesHint:
      'Ответ сохраняется значением disagree / unsure / agree — именно эти значения указывайте в условиях ветвления. Подписи кнопок на них не влияют.',
    statementColor: 'Цвет утверждения',
    statementSize: 'Размер утверждения, px',
    statementCaptionsColor: 'Цвет подписей под утверждением',
    statementCaptionsSize: 'Размер подписей под утверждением, px',

    // ── Карточка предпочтения ─────────────────────────────────────────────
    preferenceImage: 'Картинка (URL)',
    preferenceEmoji: 'Эмодзи вместо картинки',
    preferenceCaption: 'Подпись под картинкой',
    preferenceCaptionPlaceholder: 'Название книги или предложения',
    preferenceNo: 'Подпись ответа ✕',
    preferenceYes: 'Подпись ответа ✓',
    preferenceNoPlaceholder: 'Не моё',
    preferenceYesPlaceholder: 'Интересно',
    preferenceRolesHint:
      '«Подписи под вопросом» — это подпись под картинкой и подписи обоих ответов разом. Эмодзи и глифы ✕ / ✓ настройка не трогает.',
    preferenceValuesHint:
      'Ответ сохраняется значением no / yes — именно эти значения указывайте в условиях ветвления.',
    preferenceQuestionColor: 'Цвет вопроса',
    preferenceQuestionSize: 'Размер вопроса, px',
    preferenceCaptionsColor: 'Цвет подписей под вопросом',
    preferenceCaptionsSize: 'Размер подписей под вопросом, px',

    // ── Промо-билет ───────────────────────────────────────────────────────
    ticketTitle: 'Заголовок тикета',
    ticketCode: 'Код',
    ticketCodePlaceholder: 'Ваш промокод',
    ticketCodeCaption: 'Подпись под кодом',
    ticketCodeCaptionPlaceholder: 'Формулировку выбираете вы',
    ticketOldPrice: 'Старая цена',
    ticketNewPrice: 'Новая цена',
    ticketPriceHint: 'Это оформление — реальную сумму списания решает Stripe.',
    ticketShowDeadline: 'Показывать срок',
    ticketTimerAria: 'Подпись таймера (для чтеца с экрана)',
    ticketTimerAriaPlaceholder: 'Сколько осталось до конца предложения',
    ticketMinutes: 'Срок, минут',
    ticketExactDate: 'Точная дата окончания',
    ticketExactDatePlaceholder: '2026-08-01T12:00:00Z (главнее минут)',
    ticketRolesHint:
      'Заголовок и рамка билета красятся одним денежным акцентом — так билет выглядит цельно. Свой цвет заголовка эту связку разрывает; пусто — как сейчас.',
    ticketCodeColor: 'Цвет кода',
    ticketCodeSize: 'Размер кода, px',
    ticketTimerColor: 'Цвет таймера билета',
    ticketTimerSize: 'Размер таймера билета, px',
    ticketTitleColor: 'Цвет заголовка билета',
    ticketTitleSize: 'Размер заголовка билета, px',
    ticketCodeCaptionColor: 'Цвет подписи под кодом',
    ticketCodeCaptionSize: 'Размер подписи под кодом, px',
    ticketMonoHint:
      'Моно-шрифт кода и таймера не меняется: по нему их и узнают. Цены билета настройками не красятся — они работают парой «было/стало».',

    // ── Индикатор шага ────────────────────────────────────────────────────
    progressTextHint:
      'Блок печатает строку вида «Шаг 2 из 8» — номер экрана, на котором сейчас посетитель. Номер берётся из фактического положения в воронке, поэтому после переходов по правилам логики он может не совпасть с числами ниже: они нужны только как запасное значение, когда положение неизвестно (например, на превью отдельного экрана). Пока блок стоит на экране, воронка не рисует свой автоматический индикатор прогресса в шапке — чтобы счётчиков не было двух.',
    progressCurrentStep: 'Текущий шаг',
    progressTotalSteps: 'Всего шагов',
    progressStepWord: 'Слово «шаг»',

    // ── Ветвление ─────────────────────────────────────────────────────────
    branchLogicTitle: 'Переходы настраиваются в окне «Логика»',
    branchLogicText:
      'Куда покупатель попадёт после экрана, решают правила воронки: условие — и переход на экран, финал или внешний адрес. У самого блока настроек нет.',
    branchOpenLogic: 'Открыть окно «Логика»',
    branchLegacyTitle: 'Старые настройки блока не действуют',
    branchLegacyText:
      'Эти значения сохранены в воронке, но переход по ним не выполняется. Перенесите их в правила окна «Логика».',
    branchVariableName: 'Имя переменной',
    branchExpectedValue: 'Ожидаемое значение',
    branchNextScreenId: 'ID следующего экрана',

    // ── Выбор картинкой, вес и рост ───────────────────────────────────────
    imageChoiceQuestionPlaceholder: 'Введите текст вопроса...',
    imageChoiceOptions: 'Варианты (изображение + подпись)',
    imageChoiceOptionLabel: 'Подпись варианта',
    multipleChoice: 'Множественный выбор',
    weightQuestionPlaceholder: 'Какой у вас текущий вес?',
    heightQuestionPlaceholder: 'Какой у вас рост?',
    defaultUnit: 'Единица по умолчанию',
    unitKg: 'кг',
    unitLb: 'фунты',
    unitCm: 'см',
    unitFt: 'футы',
    // stylepack/unit-picker-look: вид значения и переключателя единиц.
    unitPickerLookTitle: 'Вид значения',
    unitPickerLookField: 'Поле',
    unitPickerLookBigNumber: 'Крупная цифра',
    unitPickerValueFontSize: 'Размер цифры',
    unitPickerUnderline: 'Черта под цифрой',
    unitToggleTitle: 'Переключатель единиц',
    unitToggleLookButtons: 'Кнопки',
    unitToggleLookPill: 'Пилюля',
    unitTogglePosition: 'Место переключателя',
    unitTogglePositionRight: 'Справа',
    unitTogglePositionAbove: 'Над значением',
    unitTogglePositionBelow: 'Под значением',
    unitToggleTextTransform: 'Регистр подписи единиц',
    unitToggleTextTransformNone: 'Как есть',
    unitToggleTextTransformUppercase: 'Прописные',
    unitToggleFontSize: 'Кегль подписи единиц',
    unitPickerValueColor: 'Цвет цифры',
    unitPickerUnderlineColor: 'Цвет черты',
    unitToggleActiveBg: 'Фон выбранной единицы',
    unitToggleActiveText: 'Текст выбранной единицы',
    unitToggleBg: 'Подложка переключателя',
    unitToggleText: 'Текст другой единицы',
    // Задание stylepack/unit-set: набор единиц — обе или одна.
    unitOptionsTitle: 'Единицы',
    unitOptionsBoth: 'Обе',
    unitOptionsKg: 'Только кг',
    unitOptionsLb: 'Только фунты',
    unitOptionsCm: 'Только см',
    unitOptionsFt: 'Только футы',

    // ── Экран загрузки ────────────────────────────────────────────────────
    loaderTitleLabel: 'Заголовок',
    loaderTitlePlaceholder: 'Анализируем ваши ответы...',
    loaderSkipLabel: 'Кнопка «Пропустить» в модалке',
    loaderSkipPlaceholder: 'Пусто — на языке воронки',
    loaderPopupBg: 'Фон попапа-вопроса',
    loaderPopupText: 'Цвет текста в попапе',
    loaderPopupOptions: 'Заливка вариантов в попапе',
    loaderSteps: 'Шаги загрузки',
    loaderStepsEmpty:
      'Пока шагов нет, стадии называются по секциям воронки — по тем же названиям экранов, что покупатель видел на пути сюда. Добавьте шаг, чтобы задать свои.',
    loaderAddStep: 'Добавить шаг',
    loaderMs: 'мс',
    loaderQuotesTitle: 'Карусель отзывов',
    loaderQuotesHint:
      'Пока идёт загрузка, строки ниже показываются по одной, сменяя друг друга по таймеру. Экран загрузки от этого длиннее не становится.',
    loaderQuotesEmpty:
      'Своих строк нет, и блоков «Отзывы» в воронке тоже нет — показывать карусели нечего. Добавьте строку здесь либо блок «Отзывы» на любой экран.',
    loaderAddQuote: 'Добавить строку',
    loaderQuestionsTitle: 'Вопросы во время загрузки',
    loaderQuestionsHint:
      'Вопрос показывается после одноимённой стадии (первый — после первой). Вопрос, привязанный к последней стадии, не показывается, чтобы не задерживать переход. Показ включает стиль-пак воронки.',
    loaderAddQuestion: 'Добавить вопрос',
    loaderAutoAdvance: 'Автопереход после загрузки',
    // WEB-1692 юнит 8: заголовок экрана ожидания отдельно от всего, что при нём
    // служит, — названий стадий, карточек с ответами и строки отзыва. Попап с
    // вопросом и процент не входят.
    loaderTitleColor: 'Цвет заголовка',
    loaderTitleSize: 'Размер заголовка, px',
    loaderCaptionsColor: 'Цвет стадий, карточек с ответами и отзывов',
    loaderCaptionsSize: 'Размер стадий, карточек с ответами и отзывов, px',
    loaderTextRolesHint:
      'Одна настройка на названия стадий, карточки с ответами и строку отзыва. Будущие стадии приглушаются вашим же цветом, поэтому порядок экрана ожидания по-прежнему читается. У попапа с вопросом и у процента свои настройки.',

    // ── График цели ───────────────────────────────────────────────────────
    chartTitleOptional: 'Заголовок (необязательно)',
    chartType: 'Тип графика',
    chartProgressToGoal: 'Прогресс к цели',
    chartBeforeAfter: 'Сравнение (было/стало)',
    chartStartLabel: 'Подпись начала',
    chartGoalLabel: 'Подпись цели',
    chartStartValue: 'Значение начала',
    chartGoalValue: 'Значение цели',
    chartProgressPlaceholder: 'Ваш прогресс',
    chartTodayPlaceholder: 'Сегодня',
    chartWeightNowPlaceholder: 'например, 92 кг',
    chartWeightGoalPlaceholder: 'например, 78 кг',
    chartAccentColor: 'Цвет акцента',
    // WEB-3252: вид «Столбцы» — от 2 до 5 столбцов с подписями, выделенные акцентом.
    chartKindBars: 'Столбцы',
    chartBarsList: 'Столбцы (от 2 до 5)',
    chartBarLabel: 'Подпись под столбцом',
    chartBarValue: 'Высота столбца, % (0–100)',
    chartBarValueText: 'Надпись над столбцом',
    chartBarValueTextPlaceholder: 'например, x2.2',
    chartBarHighlight: 'Выделить цветом акцента',
    chartBarAdd: 'Добавить столбец',
    // WEB-1692 юнит 8: заголовок графика отдельно от подписей шкалы. Значение
    // цели остаётся на цвете акцента — оно и есть смысл рисунка.
    chartHeadingColor: 'Цвет заголовка',
    chartHeadingSize: 'Размер заголовка, px',
    chartScaleLabelsColor: 'Цвет подписей шкалы и значения начала',
    chartScaleLabelsSize: 'Размер подписей шкалы и значения начала, px',
    chartTextRolesHint:
      'В графике-сравнении подписи нарисованы внутри самого рисунка, а рисунок растягивается по ширине экрана: заданное число работает там как пропорция, а не как точные пиксели на телефоне. Значение цели остаётся на цвете акцента.',

    // ── Согласие на cookie ────────────────────────────────────────────────
    cookieMessage: 'Текст сообщения',
    cookieMessagePlaceholder: 'Мы используем cookie, чтобы улучшить ваш опыт.',
    cookieAcceptText: 'Текст кнопки «Принять»',
    cookieDeclineText: 'Текст кнопки «Отклонить»',
    cookieAcceptPlaceholder: 'Принять',
    cookieDeclinePlaceholder: 'Отклонить',
    cookiePolicyUrl: 'URL политики cookie (необязательно)',
    cookiePosition: 'Расположение',
    // WEB-1692 юнит 7: два смысловых текста баннера — сам текст согласия и
    // надписи обеих кнопок.
    cookieTextColor: 'Цвет текста согласия',
    cookieTextSize: 'Размер текста согласия, px',
    cookieButtonsTextColor: 'Цвет надписей обеих кнопок',
    cookieButtonsTextSize: 'Размер надписей обеих кнопок, px',
    cookieTextRolesHint:
      'Обе кнопки идут одной настройкой — это пара одного действия. Ссылка «Подробнее» берёт размер текста согласия, но сохраняет свой акцентный цвет, чтобы оставаться ссылкой. Заливка кнопки настраивается полями поверхности элемента.',
    cookieBottom: 'Внизу страницы',
    cookieModal: 'Модальное окно',

    // ── Загрузка фото ─────────────────────────────────────────────────────
    photoQuestionPlaceholder: 'Загрузите фото',
    photoMaxSize: 'Макс. размер файла (МБ)',
    photoAcceptTypes: 'Допустимые типы файлов',
    photoAcceptPlaceholder: 'Выберите типы файлов',
    photoAnyImage: 'Любые изображения',

    // ── Условия оплаты ────────────────────────────────────────────────────
    paymentTermsPlaceholder:
      'Списание {{price}} ежемесячно. Отмена в любое время.',
    paymentTermsPriceTitle: 'Откуда берётся цена в тексте условий',
    paymentTermsPriceText:
      'Текст условий обязан обещать ту сумму, которую реально спишет кнопка оплаты. Поэтому цена ищется по порядку: живой выбор тарифа (на этом экране или на любом экране воронки) → своя цена главной кнопки оплаты этого экрана (главная — с «Всегда показывать», иначе последняя; её же дублирует липкая полоса) → первый блок с ценой на экране → и только потом поле ниже. Поле ниже применяется, когда взять цену больше неоткуда.',
    paymentTermsStepPlanSelector:
      'Сейчас цену задаёт «Выбор тарифа»: покупатель выбирает её сам, поле ниже не используется.',
    paymentTermsStepPayButton:
      'Сейчас цену задаёт главная кнопка оплаты этого экрана — её собственная цена. Главная — та, у которой в «Липкой кнопке вверху экрана» стоит «Всегда показывать», а если такой нет — последняя кнопка оплаты на экране. Её же дублирует липкая полоса, поэтому текст и списание совпадают. Поле ниже не используется.',
    paymentTermsStepPricedBlock:
      'Сейчас цену задаёт первый блок с ценой на этом экране. Поле ниже не используется.',
    paymentTermsStepOwnField:
      'Сейчас цена берётся из поля ниже: другого источника цены на экране нет.',

    // ── Цена по частям ────────────────────────────────────────────────────
    priceBreakdownTitle: 'Откуда берётся цена',
    pbBeforePrice: 'Текст до выбранной цены',
    pbTotalPlaceholder: 'Всего',
    pbEmptyStartsSentence: 'Пусто — цена начинает предложение.',
    pbBeforePriceColor: 'Цвет текста до цены',
    pbBeforePriceSize: 'Размер текста до цены, px',
    pbAfterPrice: 'Текст после выбранной цены',
    pbPerYearPlaceholder: 'в год',
    pbAfterPriceColor: 'Цвет текста после цены',
    pbAfterPriceSize: 'Размер текста после цены, px',
    pbBeforeDaily: 'Текст до цены в день',
    pbThatIsPlaceholder: 'Это',
    pbBeforeDailyColor: 'Цвет текста до цены в день',
    pbBeforeDailySize: 'Размер текста до цены в день, px',
    pbAfterDaily: 'Текст после цены в день',
    pbPerDayPlaceholder: 'в день',
    pbOneTimeHint:
      'У разового платежа цикла нет — строка «в день» не показывается.',
    pbAfterDailyColor: 'Цвет текста после цены в день',
    pbAfterDailySize: 'Размер текста после цены в день, px',

    // ── Даунселл-попап ────────────────────────────────────────────────────
    downsellTitle: 'Заголовок предложения',
    downsellTitlePlaceholder: 'Подождите — вот специальное предложение',
    downsellText: 'Текст предложения',
    downsellTextPlaceholder: 'Только сейчас — скидка на первый месяц',
    downsellButtonPlaceholder: 'Получить скидку',
    downsellStripePrice: 'Цена Stripe со скидкой',
    downsellShowWhen: 'Показывать при',
    downsellTriggerDeclined: 'Платёж отклонён',
    downsellTriggerExit: 'Попытка уйти со страницы',
    downsellAlwaysPlaceholder: 'Не выбрано — карточка на экране',
    downsellRolesHint:
      'Цена и надпись кнопки настраиваются отдельно ниже. Пусто — как сейчас: заголовок 18, текст 14.',
    downsellPriceColor: 'Цвет цены со скидкой',
    downsellPriceSize: 'Размер цены со скидкой, px',
    downsellTitleColor: 'Цвет заголовка предложения',
    downsellTitleSize: 'Размер заголовка предложения, px',
    downsellTextColor: 'Цвет текста предложения',
    downsellTextSize: 'Размер текста предложения, px',

    // ── Список подписок и кнопки управления ───────────────────────────────
    subsListTitle: 'Заголовок списка',
    subsEmptyText: 'Текст, если подписок нет',
    subsInnerFill: 'Заливка внутри плашки',
    subsEmptyColor: 'Цвет надписи «подписок нет»',
    subsEmptySize: 'Размер надписи «подписок нет», px',
    subsRolesHint:
      'Текст внутри плашки — это строка тарифа со статусом и строка продления под ней. Пусто — как сейчас.',
    subsListTitleColor: 'Цвет заголовка списка',
    subsListTitleSize: 'Размер заголовка списка, px',
    subsInnerTextColor: 'Цвет текста внутри плашки',
    subsInnerTextSize: 'Размер текста внутри плашки, px',
    cancelButtonText: 'Текст кнопки отмены',
    resumeButtonText: 'Текст кнопки возобновления',

    // ── Оффер удержания ───────────────────────────────────────────────────
    retentionShowOnCanvas: 'Показать на канвасе',
    retentionPreviewOffer: 'Предложение',
    retentionPreviewAccepted: 'После принятия',
    retentionPreviewNoAccess: 'Нет доступа',
    retentionPreviewEmpty: 'Пусто',
    retentionTitle: 'Заголовок оффера',
    retentionText: 'Текст оффера',
    retentionSuccessText: 'Текст после успеха',
    retentionStripePrice: 'Цена Stripe со скидкой (swap-цель)',
    retentionShowForPrices: 'Показывать для исходных цен',
    retentionAllPricesPlaceholder: 'Все цены (без условия)',
    retentionRolesHint:
      'Наполнение — это текст оффера, строка новой цены и текст после принятия скидки. Пусто — как сейчас.',
    retentionTitleColor: 'Цвет заголовка оффера',
    retentionTitleSize: 'Размер заголовка оффера, px',
    retentionBodyColor: 'Цвет наполнения',
    retentionBodySize: 'Размер наполнения, px',
    retentionButtonFill: 'Заливка внутри кнопки',
    retentionInnerFill: 'Заливка внутренней плашки',

    // ── Подстановки: нумерованные поля списков ──────────────────────────
    listItemPlaceholder: (n: number) => `Элемент ${n}`,
    optionPlaceholder: (n: number) => `Вариант ${n}`,
    loaderStepPlaceholder: (n: number) => `Шаг ${n}`,
    loaderQuotePlaceholder: (n: number) => `Строка ${n}`,
    loaderQuoteDeleteAria: (n: number) => `Удалить строку ${n}`,
    loaderQuestionPlaceholder: (n: number) => `Вопрос ${n}`,
    loaderQuestionDeleteAria: (n: number) => `Удалить вопрос ${n}`,
    loaderOptionDeleteAria: (option: number, question: number) =>
      `Удалить вариант ${option} вопроса ${question}`,
    loaderQuotesFromFunnel: (found: number) =>
      `Своих строк нет — карусель возьмёт отзывы из блоков «Отзывы» этой воронки (сейчас их найдено: ${found}). Добавьте строку, чтобы задать свои тексты.`,

    // ── Цена по частям: откуда берётся цена и что делает {{period}} ──────
    priceBreakdownText:
      'Блок показывает цену тарифа, который покупатель выбрал в воронке (тот же тариф спишет кнопка оплаты), и её же в пересчёте на день: год ÷ 365, месяц ÷ 30,4, неделя ÷ 7. Своей цены у блока нет. Пока цену взять неоткуда, блок ничего не показывает. Слово про период не пишите руками: наберите {{period}} — вместо него встанет период ВЫБРАННОГО тарифа («в месяц», «в год», «каждые 2 недели») на языке воронки — а если этого языка нет в наборе, на который мы переводим, то по-английски, и подпись не разойдётся с суммой. У разового платежа периода нет — плейсхолдер просто исчезнет. Написать слово руками по-прежнему можно, но тогда при выборе тарифов с разными периодами формулируйте нейтрально — иначе «в год» останется висеть и над месячной ценой.',
    pbPeriodHint:
      '{{period}} — период выбранного тарифа («в месяц», «в год», «каждые 2 недели»). Слово встаёт на языке воронки — том самом наборе языков, на которые заказывается перевод. Работает в любом из четырёх полей.',
    pbPeriodHintNoLocale: (language: string) =>
      `Для языка «${language}» словаря периодов у нас нет: вместо слова встанет английское («a month»). Если это не подходит, напишите период словами сами.`,
    nowGoalHint:
      'Значения и длину баров задаёте вы. Чтобы показать ответ пользователя, вставьте в текст переменную вида',
    bmiHintBefore:
      'Впишите значение сами, переключите «Откуда значение» на расчёт или подставьте переменную вида',
    bmiHintAfter:
      'Подписи зон — ваши: это ориентир, а не медицинское заключение.',

    // ── Потолки редактируемых списков (обе панели, WEB-1532/1687/1829) ───
    maxBlockOptionsHint: (max: number) =>
      `Достигнут максимум — ${max} вариантов в одном блоке. Чтобы добавить новый, удалите лишний.`,
    maxLoaderStepsHint: (max: number) =>
      `Достигнут максимум — ${max} шагов загрузки. Чтобы добавить новый, удалите лишний.`,
    maxLoaderQuestionsHint: (max: number) =>
      `Достигнут максимум — ${max} вопросов на экране загрузки. Чтобы добавить новый, удалите лишний.`,
    maxLoaderSocialProofHint: (max: number) =>
      `Достигнут максимум — ${max} строк отзывов. Чтобы добавить новую, удалите лишнюю.`,
    maxPlanSelectorPlansHint: (max: number) =>
      `Достигнут максимум — ${max} планов в одном блоке. Чтобы добавить новый, удалите лишний.`,
    maxFeatureChipsHint: (max: number) =>
      `Достигнут максимум — ${max} плашек в одном блоке. Чтобы добавить новую, удалите лишнюю.`,

    // ── Условия оплаты: поля, найденные сторожем ────────────────────────
    paymentTermsText: 'Текст условий',
    paymentTermsPlaceholdersHint: 'Доступные плейсхолдеры:',
    stripePriceLabel: 'Цена Stripe',

    // ── WEB-2451: панель говорит автору, что увидит посетитель ─────────────
    videoUrlUnrecognized:
      'Ссылка не распознана: посетитель увидит пустой блок.',
    videoAutoplayMutedOnShowOnly:
      '«Тихий автозапуск» работает только при запуске «При показе».',
    videoAutoplayMutedClickConflict:
      'При запуске «По клику» этот вариант играет без звука — выберите «Со звуком» или «Без звука».',
    countdownExpiryDateHint:
      'Когда отсчёт дойдёт до нуля, блок покажет нули, а экран и предложение останутся. Снимите предложение сами, когда акция закончится.',
    countdownExpiryMinutesHint:
      'Без даты отсчёт идёт от входа посетителя и тоже останавливается на нулях; экран и предложение остаются.',
    stockBadgeTextHint:
      'Это надпись: остаток товара не считается и сам не меняется.',
    scratchDiscountTextHint:
      'Это только надпись: цена на оплате от неё не меняется.',
    downsellNoTriggersWarning:
      'Без условий блок не всплывает: он стоит карточкой прямо на экране, и цену со скидкой видят все посетители.',
    trustBadgesRetiredNote:
      'Значок PayPal больше не показывается покупателю: на странице оплаты PayPal нет. Остальные значки показываются как раньше.',
    paymentBadgesOverlapWarning: (found: string) =>
      `На этом экране ${found} есть и во втором блоке значков оплаты: посетитель увидит эти значки дважды.`,
  },
);
